import { Clapperboard, Download, Music, Palette } from 'lucide-react'
import { useEditorStore, type ControlTab } from '../../../store/editorStore'
import { Tabs, type TabDef } from '../../common/Tabs'
import { EmptyState } from '../../common/States'
import { TabTemplate } from './TabTemplate'
import { TabSections } from './TabSections'
import { TabAudio } from './TabAudio'
import { TabExport } from './TabExport'
import { SegmentInspector } from './SegmentInspector'
import { AudioInspector } from './AudioInspector'
import { MultiSelectionInspector } from './MultiSelectionInspector'
import { ElementsList } from './ElementsList'
import { OverlayInspector } from './OverlayInspector'

/**
 * UI MK2 第2段 (UI_MK2_PHASE2.md §3): 右パネルは「いま選んでいるもの」駆動。
 * 何も選んでいなければタブは 4 つ(型と尺/セクション/音/書き出し) + 上に「要素」
 * 一覧(旧タイトル/ラベル/終了カードタブの一覧・追加機能はここへ統合)。旧タブの
 * バーは第1段から常に表示のまま（handleTabChange が選択解除も兼ねる — §3-6
 * 「第1段の handleTabChange の振る舞いを維持」）だったので、その骨格を踏襲し、
 * 中身だけ選択種類ごとのインスペクタに差し替える。
 */
const TABS: TabDef<ControlTab>[] = [
  { id: 'template', label: '型と尺', icon: <Clapperboard size={15} /> },
  { id: 'sections', label: 'セクション', icon: <Palette size={15} /> },
  { id: 'audio', label: '音', icon: <Music size={15} /> },
  { id: 'export', label: '書き出し', icon: <Download size={15} /> },
]

export function ControlPanel() {
  const activeTab = useEditorStore((s) => s.activeTab)
  const setActiveTab = useEditorStore((s) => s.setActiveTab)
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const clearSelection = useEditorStore((s) => s.clearSelection)
  const composition = useEditorStore((s) => s.composition)

  // タブをクリックしたら選択を解除して全体設定へ戻る(第1段からの既存挙動を維持)。
  function handleTabChange(tab: ControlTab) {
    if (selectedIds.length > 0) clearSelection()
    setActiveTab(tab)
  }

  const single = selectedIds.length === 1 ? selectedIds[0] : null
  const selectedOverlay =
    single?.kind === 'overlay' ? (composition?.tracks.overlays.find((o) => o.id === single.id) ?? null) : null

  return (
    <aside
      className="w-full h-full shrink-0 border-l border-ink-border flex flex-col bg-ink-surface/40"
      data-testid="control-panel"
    >
      <Tabs tabs={TABS} active={activeTab} onChange={handleTabChange} />
      <div className="flex-1 overflow-y-auto flex flex-col">
        {selectedIds.length > 1 ? (
          <MultiSelectionInspector />
        ) : single ? (
          single.kind === 'segment' ? (
            <SegmentInspector segmentId={single.id} />
          ) : single.kind === 'audio' ? (
            <AudioInspector audioId={single.id} />
          ) : selectedOverlay ? (
            <OverlayInspector overlay={selectedOverlay} />
          ) : (
            // undo/redo 等で選択中の id が Composition から消えた後の防御的フォールバック
            // (黙って空白を返さない — CLAUDE.md「サイレント禁止」)。
            <EmptyState title="選択した要素が見つかりません" description="選択を解除してください。" />
          )
        ) : (
          <>
            <ElementsList />
            {activeTab === 'template' && <TabTemplate />}
            {activeTab === 'sections' && <TabSections />}
            {activeTab === 'audio' && <TabAudio />}
            {activeTab === 'export' && <TabExport />}
          </>
        )}
      </div>
    </aside>
  )
}
